import React, { useState } from 'react';
import styled from 'styled-components';
import { Users, Plus, Edit, Trash2, Search, X, Calendar, Clock, TrendingUp, Award, FileText, CheckCircle } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input, InputGroup } from '../components/common/Input';
import { theme } from '../styles/theme';

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${theme.colors.background};
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 260px;
  padding: ${theme.spacing.xl};
  padding-bottom: 80px;

  @media (max-width: ${theme.breakpoints.lg}) {
    margin-left: 0;
    padding: ${theme.spacing.md};
    padding-top: 70px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.sm};
    padding-top: 70px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
  
  h1 {
    font-size: ${theme.typography.fontSize['3xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  border-bottom: 2px solid ${theme.colors.border};
  flex-wrap: wrap;
`;

const Tab = styled.button`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: none;
  background: transparent;
  border-bottom: 3px solid ${({ active }) => active ? theme.colors.primary : 'transparent'};
  color: ${({ active }) => active ? theme.colors.primary : theme.colors.textSecondary};
  font-weight: ${({ active }) => active ? theme.typography.fontWeight.semiBold : theme.typography.fontWeight.regular};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.typography.fontSize.base};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const ActionsBar = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  padding: ${theme.spacing.lg};
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
`;

const TableContainer = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: ${theme.colors.secondary};
`;

const TableHeaderRow = styled.tr`
  border-bottom: 2px solid ${theme.colors.border};
`;

const TableHeaderCell = styled.th`
  padding: ${theme.spacing.lg};
  text-align: left;
  font-weight: ${theme.typography.fontWeight.semiBold};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid ${theme.colors.border};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${theme.colors.secondary};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: ${theme.spacing.lg};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text};
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semiBold};
  background-color: ${({ role }) => {
    if (role === 'admin') return `${theme.colors.primary}20`;
    if (role === 'waiter') return `${theme.colors.status.preparing}20`;
    if (role === 'chef') return `${theme.colors.accent}20`;
    return `${theme.colors.textLight}20`;
  }};
  color: ${({ role }) => {
    if (role === 'admin') return theme.colors.primary;
    if (role === 'waiter') return theme.colors.status.preparing;
    if (role === 'chef') return theme.colors.accent;
    return theme.colors.text;
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const ActionButton = styled.button`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border};
  background-color: ${theme.colors.white};
  color: ${theme.colors.text};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.sm};

  &:hover {
    background-color: ${theme.colors.secondary};
    border-color: ${theme.colors.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.modal};
  padding: ${theme.spacing.lg};
`;

const ModalContent = styled(Card)`
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};

  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semiBold};
    color: ${theme.colors.text};
  }
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.md};
  border: none;
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.text};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.status.outOfStock};
    color: ${theme.colors.white};
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${theme.spacing.lg};
`;

// Mock staff data
const initialStaff = [
  {
    id: 1,
    name: 'John Doe',
    email: 'john@restaurant.com',
    phone: '+91 9876543210',
    role: 'waiter',
    shift: 'Morning',
    status: 'active',
  },
  {
    id: 2,
    name: 'Jane Smith',
    email: 'jane@restaurant.com',
    phone: '+91 9876543211',
    role: 'chef',
    shift: 'Evening',
    status: 'active',
  },
  {
    id: 3,
    name: 'Mike Johnson',
    email: 'mike@restaurant.com',
    phone: '+91 9876543212',
    role: 'waiter',
    shift: 'Night',
    status: 'active',
  },
];

const roles = ['admin', 'waiter', 'chef', 'manager', 'counter'];
const shifts = ['Morning', 'Evening', 'Night', 'Full Day'];

function StaffManagement() {
  const [activeTab, setActiveTab] = useState('staff');
  const [staff, setStaff] = useState(initialStaff);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'waiter',
    shift: 'Morning',
    status: 'active',
  });

  // Mock data for additional features
  const attendanceData = [
    { id: 1, staffName: 'John Doe', date: '2024-12-20', checkIn: '09:00 AM', checkOut: '06:00 PM', status: 'present' },
    { id: 2, staffName: 'Jane Smith', date: '2024-12-20', checkIn: '10:00 AM', checkOut: '07:00 PM', status: 'present' },
  ];

  const shiftSchedule = [
    { id: 1, staffName: 'John Doe', date: '2024-12-21', shift: 'Morning', startTime: '09:00 AM', endTime: '05:00 PM' },
    { id: 2, staffName: 'Jane Smith', date: '2024-12-21', shift: 'Evening', startTime: '02:00 PM', endTime: '10:00 PM' },
  ];

  const performanceData = [
    { id: 1, staffName: 'John Doe', ordersServed: 45, avgRating: 4.8, totalSales: 12500, commission: 625 },
    { id: 2, staffName: 'Jane Smith', ordersServed: 32, avgRating: 4.9, totalSales: 8900, commission: 445 },
  ];

  const leaveRequests = [
    { id: 1, staffName: 'John Doe', type: 'Sick Leave', startDate: '2024-12-25', endDate: '2024-12-26', status: 'pending' },
    { id: 2, staffName: 'Mike Johnson', type: 'Personal Leave', startDate: '2024-12-28', endDate: '2024-12-29', status: 'approved' },
  ];

  const trainingRecords = [
    { id: 1, staffName: 'John Doe', course: 'Food Safety', completed: true, date: '2024-11-15', certificate: 'Yes' },
    { id: 2, staffName: 'Jane Smith', course: 'Customer Service', completed: true, date: '2024-11-20', certificate: 'Yes' },
  ];

  const handleAddStaff = () => {
    setEditingStaff(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: 'waiter',
      shift: 'Morning',
      status: 'active',
    });
    setShowModal(true);
  };

  const handleEdit = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      name: staffMember.name,
      email: staffMember.email,
      phone: staffMember.phone,
      role: staffMember.role,
      shift: staffMember.shift,
      status: staffMember.status,
    });
    setShowModal(true);
  };

  const handleDelete = (staffId) => {
    if (window.confirm('Are you sure you want to delete this staff member?')) {
      setStaff((prev) => prev.filter((member) => member.id !== staffId));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingStaff) {
      setStaff((prev) =>
        prev.map((member) =>
          member.id === editingStaff.id
            ? { ...member, ...formData }
            : member
        )
      );
    } else {
      const newStaff = {
        id: Date.now(),
        ...formData,
      };
      setStaff((prev) => [...prev, newStaff]);
    }
    setShowModal(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: 'waiter',
      shift: 'Morning',
      status: 'active',
    });
  };

  const filteredStaff = staff.filter((member) =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container>
      <Sidebar />
      <MainContent>
        <Header>
          <h1>Staff Management</h1>
          {activeTab === 'staff' && (
            <Button variant="primary" onClick={handleAddStaff}>
              <Plus size={20} />
              Add Staff Member
            </Button>
          )}
        </Header>

        <Tabs>
          <Tab active={activeTab === 'staff'} onClick={() => setActiveTab('staff')}>
            <Users size={18} />
            Staff List
          </Tab>
          <Tab active={activeTab === 'attendance'} onClick={() => setActiveTab('attendance')}>
            <CheckCircle size={18} />
            Attendance
          </Tab>
          <Tab active={activeTab === 'shifts'} onClick={() => setActiveTab('shifts')}>
            <Clock size={18} />
            Shift Scheduling
          </Tab>
          <Tab active={activeTab === 'performance'} onClick={() => setActiveTab('performance')}>
            <TrendingUp size={18} />
            Performance
          </Tab>
          <Tab active={activeTab === 'commission'} onClick={() => setActiveTab('commission')}>
            <Award size={18} />
            Commission
          </Tab>
          <Tab active={activeTab === 'leave'} onClick={() => setActiveTab('leave')}>
            <Calendar size={18} />
            Leave Management
          </Tab>
          <Tab active={activeTab === 'training'} onClick={() => setActiveTab('training')}>
            <FileText size={18} />
            Training
          </Tab>
        </Tabs>

        {activeTab === 'staff' && (
          <>
            <ActionsBar>
              <Input
                type="text"
                placeholder="Search staff..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ flex: 1, maxWidth: '400px' }}
              />
            </ActionsBar>

            <TableContainer>
          <Table>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Phone</TableHeaderCell>
                <TableHeaderCell>Role</TableHeaderCell>
                <TableHeaderCell>Shift</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {filteredStaff.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="7" style={{ textAlign: 'center', padding: theme.spacing['2xl'] }}>
                    No staff members found
                  </TableCell>
                </TableRow>
              ) : (
                filteredStaff.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell><strong>{member.name}</strong></TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>{member.phone}</TableCell>
                    <TableCell>
                      <RoleBadge role={member.role}>
                        {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                      </RoleBadge>
                    </TableCell>
                    <TableCell>{member.shift}</TableCell>
                    <TableCell>
                      <RoleBadge role={member.status === 'active' ? 'waiter' : 'chef'}>
                        {member.status}
                      </RoleBadge>
                    </TableCell>
                    <TableCell>
                      <ActionButtons>
                        <ActionButton onClick={() => handleEdit(member)}>
                          <Edit />
                          Edit
                        </ActionButton>
                        <ActionButton onClick={() => handleDelete(member.id)}>
                          <Trash2 />
                          Delete
                        </ActionButton>
                      </ActionButtons>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
          </>
        )}

        {activeTab === 'attendance' && (
          <TableContainer>
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Staff Name</TableHeaderCell>
                  <TableHeaderCell>Date</TableHeaderCell>
                  <TableHeaderCell>Check In</TableHeaderCell>
                  <TableHeaderCell>Check Out</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {attendanceData.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell><strong>{record.staffName}</strong></TableCell>
                    <TableCell>{new Date(record.date).toLocaleDateString()}</TableCell>
                    <TableCell>{record.checkIn}</TableCell>
                    <TableCell>{record.checkOut}</TableCell>
                    <TableCell>
                      <RoleBadge role={record.status === 'present' ? 'waiter' : 'chef'}>
                        {record.status}
                      </RoleBadge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {activeTab === 'shifts' && (
          <TableContainer>
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Staff Name</TableHeaderCell>
                  <TableHeaderCell>Date</TableHeaderCell>
                  <TableHeaderCell>Shift</TableHeaderCell>
                  <TableHeaderCell>Start Time</TableHeaderCell>
                  <TableHeaderCell>End Time</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {shiftSchedule.map((schedule) => (
                  <TableRow key={schedule.id}>
                    <TableCell><strong>{schedule.staffName}</strong></TableCell>
                    <TableCell>{new Date(schedule.date).toLocaleDateString()}</TableCell>
                    <TableCell>{schedule.shift}</TableCell>
                    <TableCell>{schedule.startTime}</TableCell>
                    <TableCell>{schedule.endTime}</TableCell>
                    <TableCell>
                      <ActionButton onClick={() => alert('Edit shift')}>
                        <Edit /> Edit
                      </ActionButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {activeTab === 'performance' && (
          <TableContainer>
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Staff Name</TableHeaderCell>
                  <TableHeaderCell>Orders Served</TableHeaderCell>
                  <TableHeaderCell>Avg Rating</TableHeaderCell>
                  <TableHeaderCell>Total Sales</TableHeaderCell>
                  <TableHeaderCell>Commission</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {performanceData.map((perf) => (
                  <TableRow key={perf.id}>
                    <TableCell><strong>{perf.staffName}</strong></TableCell>
                    <TableCell>{perf.ordersServed}</TableCell>
                    <TableCell>{perf.avgRating} ⭐</TableCell>
                    <TableCell>₹{perf.totalSales.toLocaleString()}</TableCell>
                    <TableCell>₹{perf.commission.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {activeTab === 'commission' && (
          <Card>
            <h2 style={{ marginBottom: theme.spacing.lg }}>Commission Settings</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
              <div>
                <h3>Commission Structure</h3>
                <p>Waiters: 5% of total sales</p>
                <p>Chefs: 2% of total sales</p>
                <p>Managers: 3% of total sales</p>
              </div>
              <div style={{ marginTop: theme.spacing.lg }}>
                <h3>Current Month Commission</h3>
                <TableContainer>
                  <Table>
                    <TableHeader>
                      <TableHeaderRow>
                        <TableHeaderCell>Staff Name</TableHeaderCell>
                        <TableHeaderCell>Total Sales</TableHeaderCell>
                        <TableHeaderCell>Commission Rate</TableHeaderCell>
                        <TableHeaderCell>Commission Amount</TableHeaderCell>
                      </TableHeaderRow>
                    </TableHeader>
                    <TableBody>
                      {performanceData.map((perf) => (
                        <TableRow key={perf.id}>
                          <TableCell><strong>{perf.staffName}</strong></TableCell>
                          <TableCell>₹{perf.totalSales.toLocaleString()}</TableCell>
                          <TableCell>5%</TableCell>
                          <TableCell>₹{perf.commission.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </div>
          </Card>
        )}

        {activeTab === 'leave' && (
          <TableContainer>
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Staff Name</TableHeaderCell>
                  <TableHeaderCell>Leave Type</TableHeaderCell>
                  <TableHeaderCell>Start Date</TableHeaderCell>
                  <TableHeaderCell>End Date</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {leaveRequests.map((leave) => (
                  <TableRow key={leave.id}>
                    <TableCell><strong>{leave.staffName}</strong></TableCell>
                    <TableCell>{leave.type}</TableCell>
                    <TableCell>{new Date(leave.startDate).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(leave.endDate).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <RoleBadge role={leave.status === 'approved' ? 'waiter' : 'chef'}>
                        {leave.status}
                      </RoleBadge>
                    </TableCell>
                    <TableCell>
                      {leave.status === 'pending' && (
                        <>
                          <ActionButton onClick={() => alert('Approved')}>
                            <CheckCircle /> Approve
                          </ActionButton>
                          <ActionButton onClick={() => alert('Rejected')}>
                            <X /> Reject
                          </ActionButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {activeTab === 'training' && (
          <TableContainer>
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Staff Name</TableHeaderCell>
                  <TableHeaderCell>Course</TableHeaderCell>
                  <TableHeaderCell>Completed</TableHeaderCell>
                  <TableHeaderCell>Date</TableHeaderCell>
                  <TableHeaderCell>Certificate</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {trainingRecords.map((training) => (
                  <TableRow key={training.id}>
                    <TableCell><strong>{training.staffName}</strong></TableCell>
                    <TableCell>{training.course}</TableCell>
                    <TableCell>
                      {training.completed ? (
                        <RoleBadge role="waiter">Yes</RoleBadge>
                      ) : (
                        <RoleBadge role="chef">No</RoleBadge>
                      )}
                    </TableCell>
                    <TableCell>{new Date(training.date).toLocaleDateString()}</TableCell>
                    <TableCell>{training.certificate}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {showModal && (
          <Modal onClick={() => setShowModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h2>{editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}</h2>
                <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
              </ModalHeader>
              <ModalForm onSubmit={handleSubmit}>
                <InputGroup>
                  <label>Full Name</label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </InputGroup>
                <InputGroup>
                  <label>Email</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </InputGroup>
                <InputGroup>
                  <label>Phone</label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </InputGroup>
                <InputGroup>
                  <label>Role</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      fontSize: theme.typography.fontSize.base,
                      fontFamily: theme.typography.fontFamily,
                      border: `2px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.md,
                      backgroundColor: theme.colors.white,
                    }}
                    required
                  >
                    {roles.map((role) => (
                      <option key={role} value={role}>
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </option>
                    ))}
                  </select>
                </InputGroup>
                <InputGroup>
                  <label>Shift</label>
                  <select
                    value={formData.shift}
                    onChange={(e) => setFormData({ ...formData, shift: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      fontSize: theme.typography.fontSize.base,
                      fontFamily: theme.typography.fontFamily,
                      border: `2px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.md,
                      backgroundColor: theme.colors.white,
                    }}
                    required
                  >
                    {shifts.map((shift) => (
                      <option key={shift} value={shift}>
                        {shift}
                      </option>
                    ))}
                  </select>
                </InputGroup>
                <InputGroup>
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      fontSize: theme.typography.fontSize.base,
                      fontFamily: theme.typography.fontFamily,
                      border: `2px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.md,
                      backgroundColor: theme.colors.white,
                    }}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </InputGroup>
                <ButtonGroup>
                  <Button variant="secondary" type="button" onClick={() => setShowModal(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit">
                    {editingStaff ? 'Update' : 'Add'} Staff Member
                  </Button>
                </ButtonGroup>
              </ModalForm>
            </ModalContent>
          </Modal>
        )}
      </MainContent>
      <Footer />
    </Container>
  );
}

export default StaffManagement;

